// Module 2 / Slice 9 — customer-config admin grid.
// Plan §10 screen 3 / ADR 0009. Filter by kind; infra_secret rows are
// always read-only with the "Managed in AWS Secrets Manager" pill.

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { referenceApiClient } from "../../api/client";
import { useI18n } from "../../i18n/I18nContext";

type KindFilter = "policy" | "feature" | "infra_secret" | "all";

export function CustomerConfigAdminScreen(): JSX.Element {
  const { t } = useI18n();
  const [kindFilter, setKindFilter] = useState<KindFilter>("all");

  const query = useQuery({
    queryKey: ["reference", "customer-config", kindFilter],
    queryFn: async () => {
      const response = await referenceApiClient.GET("/reference/customer-config", {
        params: {
          query: {
            limit: 200,
            kind: kindFilter === "all" ? undefined : kindFilter,
          },
        },
      });
      if (!response.data) {
        throw new Error("reference_customerconfig_fetch_failed");
      }
      return response.data;
    },
  });

  return (
    <div>
      <h1>{t("reference.customerconfig.heading")}</h1>
      <p className="rc-help">{t("reference.customerconfig.description")}</p>
      <div role="radiogroup" aria-label={t("reference.customerconfig.kindFilter")}>
        {(["all", "policy", "feature", "infra_secret"] as const).map((k) => (
          <label key={k} className="rc-radio">
            <input
              type="radio"
              name="kind"
              checked={kindFilter === k}
              onChange={() => setKindFilter(k)}
            />
            {t(`reference.customerconfig.kind.${k}`)}
          </label>
        ))}
      </div>
      {query.isLoading && <p>{t("login.discovering")}</p>}
      {query.isError && (
        <p role="alert" style={{ color: "var(--rc-color-danger)" }}>
          {(query.error as Error).message}
        </p>
      )}
      {query.data && query.data.items && (
        <table className="rc-table">
          <thead>
            <tr>
              <th>{t("reference.customerconfig.column.key")}</th>
              <th>{t("reference.customerconfig.column.kind")}</th>
              <th>{t("reference.customerconfig.column.value")}</th>
              <th>{t("reference.customerconfig.column.locked")}</th>
            </tr>
          </thead>
          <tbody>
            {query.data.items.map((entry) => (
              <tr key={entry.id}>
                <td><code>{entry.key}</code></td>
                <td>{entry.kind}</td>
                <td>
                  {entry.kind === "infra_secret" ? (
                    <span className="rc-pill rc-pill--info">
                      {t("reference.customerconfig.secretManaged")}
                      {entry.secretName ? <>: <code>{entry.secretName}</code></> : null}
                    </span>
                  ) : (
                    <code>{JSON.stringify(entry.value)}</code>
                  )}
                </td>
                <td>{entry.isLocked ? "\u2714" : "\u2014"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
